import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Brain, Pencil, Sparkles, Loader2, History } from "lucide-react";
import api from "../../api";

// Case formulation (backend clinical/care_plan/formulation_routes.py). Clinician-only.
// `compact` = the read-only summary shown beside a consultation.
export default function FormulationPanel({ patientId, compact = false, consultationId = null }) {
  const [state, setState] = useState(null); // {current, layouts}
  const [draft, setDraft] = useState(null);  // {model, fields, ai_assisted}
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = () => api.getFormulation(patientId).then((r) => setState(r.data)).catch(() => setState({ current: null, layouts: {} }));
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line

  if (!state) return compact ? null : <p className="text-sm text-secondary-500">Loading…</p>;
  const { current, layouts } = state;
  const shown = viewing || current;

  if (compact) {
    if (!current) return <p className="text-xs text-secondary-500">No formulation yet - write one on the patient file.</p>;
    const L = layouts[current.model];
    return (
      <dl className="space-y-2">
        {(L?.fields || []).filter((f) => current.fields[f.key]).map((f) => (
          <div key={f.key}><dt className="text-xs font-extrabold uppercase tracking-wide text-secondary-900">{f.label}</dt><dd className="text-sm text-secondary-800 whitespace-pre-line">{current.fields[f.key]}</dd></div>
        ))}
      </dl>
    );
  }

  const start = (model) => setDraft({ model: model || current?.model || "five_ps", fields: current && (!model || model === current.model) ? { ...current.fields } : {}, ai_assisted: false });
  const aiDraft = async () => {
    setAiBusy(true);
    try {
      const r = await api.draftFormulation(patientId, draft.model);
      setDraft((d) => ({ ...d, fields: r.data.fields, ai_assisted: true }));
      toast.info("AI draft added - check every line against what you know before saving.");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't get a draft.");
    } finally {
      setAiBusy(false);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.saveFormulation(patientId, { ...draft, consultation_id: consultationId || undefined });
      setDraft(null); setViewing(null); setHistory(null);
      toast.success("Formulation saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save it.");
    } finally {
      setSaving(false);
    }
  };

  if (draft) {
    const L = layouts[draft.model];
    return (
      <div className="rounded-xl bg-white border border-secondary-100 p-4 md:p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex items-center gap-2 text-base font-bold text-secondary-900"><Brain className="w-5 h-5 text-primary-600" /> {current ? `Update formulation (version ${current.version + 1})` : "New formulation"}</h3>
          <select value={draft.model} onChange={(e) => setDraft({ model: e.target.value, fields: {}, ai_assisted: false })} aria-label="Layout" className="rounded-md border border-secondary-300 px-2 py-1 text-sm bg-white">
            {Object.entries(layouts).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
          </select>
          <button type="button" onClick={aiDraft} disabled={aiBusy} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-800 disabled:opacity-50">
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Draft from session notes
          </button>
        </div>
        {draft.ai_assisted && <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">AI draft from the recent notes, questionnaires and risk history. It may miss or misread things - edit freely; only you decide what's saved.</p>}
        {(L?.fields || []).map((f) => (
          <div key={f.key}>
            <p className="text-sm font-semibold text-secondary-800">{f.label}</p>
            <p className="text-xs text-secondary-500 mb-1">{f.hint}</p>
            <textarea value={draft.fields[f.key] || ""} onChange={(e) => setDraft((d) => ({ ...d, fields: { ...d.fields, [f.key]: e.target.value } }))}
              rows={draft.model === "custom" ? 10 : 3} aria-label={f.label} className="w-full rounded-md border border-secondary-300 p-2 text-sm" />
          </div>
        ))}
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save formulation"}</button>
          <button type="button" onClick={() => setDraft(null)} className="rounded-lg border border-secondary-300 px-4 py-2 text-sm">Cancel</button>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="rounded-xl bg-white border border-secondary-100 p-6 text-center space-y-3">
        <Brain className="w-8 h-8 text-primary-400 mx-auto" />
        <p className="text-sm text-secondary-600">No formulation yet. Pull together what you understand about why these difficulties started and what keeps them going.</p>
        <div className="flex flex-wrap justify-center gap-2">
          {Object.entries(layouts).map(([k, v]) => <button key={k} type="button" onClick={() => start(k)} className="rounded-lg border border-primary-300 px-3 py-1.5 text-sm font-semibold text-primary-700 hover:bg-primary-50">{v.name}</button>)}
        </div>
      </div>
    );
  }

  const L = layouts[shown.model];
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-white border border-secondary-100 p-4 md:p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Brain className="w-5 h-5 text-primary-600" />
          <h3 className="text-base font-bold text-secondary-900">Case formulation</h3>
          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-bold text-secondary-700">{L?.name}</span>
          <span className="text-xs text-secondary-600">version {shown.version} · {new Date(shown.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{shown.created_by_name ? ` · ${shown.created_by_name}` : ""}{shown.ai_assisted ? " · AI-assisted" : ""}</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={() => (history ? setHistory(null) : api.getFormulationHistory(patientId).then((r) => setHistory(r.data)))} className="inline-flex items-center gap-1 rounded-lg border border-secondary-300 px-3 py-1.5 text-xs font-semibold"><History className="w-3.5 h-3.5" /> Versions</button>
            <button type="button" onClick={() => start()} className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white"><Pencil className="w-3.5 h-3.5" /> Update</button>
          </div>
        </div>
        {viewing && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">Viewing an older version. <button type="button" onClick={() => setViewing(null)} className="font-semibold underline">Back to current</button></p>}
        {history && (
          <ul className="flex flex-wrap gap-1.5">
            {history.map((h) => (
              <li key={h.id}><button type="button" onClick={() => setViewing(h.id === current.id ? null : h)}
                className={`rounded-full border px-2.5 py-1 text-xs ${shown.id === h.id ? "border-primary-500 bg-primary-50 text-primary-800" : "border-secondary-300 text-secondary-700"}`}>
                v{h.version} · {new Date(h.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</button></li>
            ))}
          </ul>
        )}
        <div className={`grid gap-3 ${shown.model === "custom" ? "" : "md:grid-cols-2"}`}>
          {(L?.fields || []).map((f) => (
            <div key={f.key} className="rounded-lg border border-secondary-100 bg-secondary-50 p-3">
              <p className="text-xs font-extrabold uppercase tracking-wide text-secondary-900">{f.label}</p>
              <p className="mt-1 text-sm text-secondary-800 whitespace-pre-line">{shown.fields[f.key] || <span className="text-secondary-400">-</span>}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
